/**
 * The public pay page: OPEN-212 PR C (DEC-387 D2). The "Pay by bank" link in a Paelode invoice
 * email opens /pay/<token> here, with no sign-in (main.tsx renders this page before the app's
 * auth gate). It shows what the email already says and sends the payer to Stripe's hosted
 * Checkout, which shows the bank authorisation (Stripe's NACHA mandate text) and verifies the
 * account. Stripe returns the payer to /pay/<token>?done=1, or to /pay/done for a link Paelode
 * staff sent by hand.
 *
 *   GET  /v1/pay/:token           the summary and whether it can be paid now
 *   POST /v1/pay/:token/session   the Checkout URL to go to
 */
import * as React from 'react';

export interface PayPageSummary {
  readonly invoiceNumber: string | null;
  readonly from: string;
  readonly to: string;
  readonly totalCents: number;
  readonly amountDueCents: number;
  readonly dueDate: string | null;
  readonly state: 'OPEN' | 'SETTLING' | 'PAID' | 'VOID';
  readonly payable: boolean;
}

const money = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const card: React.CSSProperties = {
  maxWidth: '440px',
  margin: '48px auto',
  padding: '28px',
  borderRadius: '10px',
  background: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border-primary)',
};

function Shell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <main style={{ padding: '0 16px' }} aria-label="Pay an invoice">
      <div style={card}>
        <h1 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 12px' }}>{title}</h1>
        {children}
      </div>
    </main>
  );
}

const muted: React.CSSProperties = { color: 'var(--color-text-secondary)', fontSize: '14px' };

/** The token from /pay/<token>, or null for /pay/done and anything else. */
export function payTokenFrom(pathname: string): string | null {
  const m = /^\/pay\/([^/]+)\/?$/.exec(pathname);
  if (m === null || m[1] === 'done') return null;
  return decodeURIComponent(m[1]!);
}

export function PublicPayPage({
  apiBaseUrl,
  pathname,
  search,
}: {
  apiBaseUrl: string;
  pathname: string;
  search: string;
}): React.JSX.Element {
  const token = payTokenFrom(pathname);
  const submitted = new URLSearchParams(search).get('done') === '1' || pathname === '/pay/done';
  const [summary, setSummary] = React.useState<PayPageSummary | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [going, setGoing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Back from Stripe: the page only says thank you, so there is nothing to load.
    if (token === null || submitted) return;
    let live = true;
    fetch(`${apiBaseUrl}/v1/pay/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as PayPageSummary;
        if (live) setSummary(body);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [apiBaseUrl, token, submitted]);

  if (submitted) {
    return (
      <Shell title="Payment submitted">
        <p style={muted} data-testid="pay-submitted">
          Thank you. Your bank payment is on its way. ACH payments usually take about 4 business
          days to settle; you do not need to do anything else.
        </p>
      </Shell>
    );
  }
  if (token === null || failed) {
    return (
      <Shell title="This pay link does not work">
        <p style={muted} data-testid="pay-bad-link">
          It may have expired. Reply to the invoice email, or contact Paelode Systems billing, for a
          new one.
        </p>
      </Shell>
    );
  }
  if (summary === null) return <Shell title="Loading…">{null}</Shell>;

  const pay = (): void => {
    setGoing(true);
    setError(null);
    fetch(`${apiBaseUrl}/v1/pay/${encodeURIComponent(token)}/session`, { method: 'POST' })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const { url } = (await res.json()) as { url: string };
        window.location.assign(url);
      })
      .catch(() => {
        setGoing(false);
        setError('Could not start the payment. Try again in a minute.');
      });
  };

  return (
    <Shell title={`Invoice ${summary.invoiceNumber ?? ''}`}>
      <p style={muted}>
        From {summary.from} to {summary.to}
      </p>
      {summary.state === 'OPEN' && (
        <>
          <p style={{ fontSize: '28px', fontWeight: 600, margin: '12px 0 4px' }}>
            {money(summary.amountDueCents)}
          </p>
          {summary.dueDate !== null && <p style={muted}>Due {summary.dueDate}</p>}
          {summary.payable ? (
            <>
              <button
                type="button"
                onClick={pay}
                disabled={going}
                style={{
                  marginTop: '16px',
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '16px',
                  cursor: going ? 'default' : 'pointer',
                  background: 'var(--color-accent-blue)',
                  color: 'var(--color-on-accent)',
                }}
              >
                {going ? 'Opening…' : 'Pay by bank'}
              </button>
              <p style={{ ...muted, fontSize: '12px', marginTop: '10px' }}>
                You pay from your bank account (ACH) on Stripe&apos;s secure page. No fee to you.
              </p>
            </>
          ) : (
            <p style={muted}>Online payment is not available yet. Pay as the invoice says.</p>
          )}
          {error !== null && (
            <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
              {error}
            </p>
          )}
        </>
      )}
      {summary.state === 'SETTLING' && (
        <p style={muted} data-testid="pay-settling">
          A bank payment for this invoice is already on its way. Nothing more to do.
        </p>
      )}
      {summary.state === 'PAID' && (
        <p style={muted} data-testid="pay-paid">
          This invoice is paid. Thank you.
        </p>
      )}
      {summary.state === 'VOID' && (
        <p style={muted} data-testid="pay-void">
          This invoice was cancelled. Nothing is owed on it.
        </p>
      )}
    </Shell>
  );
}
